/**
 * roles.ts
 * Role-Based Access Control (RBAC) utilities.
 *
 * Three tiers exist in this system:
 *  - ADMIN        Full access to every route.
 *  - MANAGER      Approvers at every stage of the approval process: card activators,
 *                 Security Specialists, Supervisors, ESM, etc.
 *                 Access: Dashboard, Requests, Reports, Delegation.
 *  - NORMAL_USER  Regular employees who can only create / track their own requests.
 *                 Access: Requests only.
 */

// ─── Role Enum ──────────────────────────────────────────────────────────────

export type UserRole = 'ADMIN' | 'MANAGER' | 'NORMAL_USER';

// ─── Access Map ──────────────────────────────────────────────────────────────

/**
 * Every path fragment (without trailing slash) that a role is permitted to visit.
 * Sub-routes (e.g. /addrequest, /editrequest) are grouped under their parent key.
 */
export const ROLE_ACCESS: Record<UserRole, string[]> = {
  ADMIN: [
    '/dashboard',
    '/requestlist', '/addrequest', '/editrequest',
    '/sitelist', '/addsite', '/editsite',
    '/doorlist', '/adddoor', '/editdoor',
    '/approverlist', '/addapprover', '/editapprover',
    '/delegationlistbyadmin', '/adddelegationadmin',
    '/reports',
    '/employeelist',
    '/roles',
  ],
  MANAGER: [
    '/dashboard',
    '/requestlist', '/addrequest', '/editrequest',
    '/delegationlistbyadmin', '/adddelegationadmin',
    '/reports',
  ],
  NORMAL_USER: [
    '/requestlist', '/addrequest', '/editrequest',
  ],
};

// ─── Role Determination ──────────────────────────────────────────────────────

/**
 * Job role keywords that qualify a user as a Manager / Approver.
 * This list mirrors the logic already used in `canAccessDelegation`.
 */
const MANAGER_JOB_ROLES = [
  'manager',
  'esm',                     // External Security Manager
  'security supervisor',
  'security specialist',
  'supervisor',
  'card activator',
  'activator',
  'officer',
  'executive',
  'excut',                   // In case of typos like "excutives"
];

const MANAGER_ROLE_NAMES = ['printer', 'activator', 'admin'];
const MANAGER_PERMISSIONS = ['activate:requests'];

/**
 * Derive a `UserRole` from the raw user object stored in Redux / JWT.
 * Falls back gracefully to NORMAL_USER for any unrecognised user.
 */
export function getUserRole(user: any): UserRole {
  if (!user) return 'NORMAL_USER';

  const rolesList: any[] = user.roles || (user.role ? [user.role] : []);
  const roleNames = rolesList.map(r => (r.name || '').toLowerCase());
  const isFullAdminRole = roleNames.some(name => 
    name === "global admin" || 
    name === "admin" || 
    name === "administrator" || 
    name === "full administrator"
  );

  // 1. Explicit admin flag with administrator role (or no roles) takes ADMIN priority
  if (user.isAdmin === true && (rolesList.length === 0 || isFullAdminRole)) {
    return 'ADMIN';
  }

  // 2. Check for Manager / Approver indicators
  const jobRole: string  = (user.jobRole  || '').toLowerCase();
  
  const permissions: string[] = [];
  rolesList.forEach(r => {
    if (r?.permissions && Array.isArray(r.permissions)) {
      r.permissions.forEach((p: string) => permissions.push(p));
    }
  });

  const isManagerByJobRole  = MANAGER_JOB_ROLES.some(r => jobRole.includes(r));
  const isManagerByRoleName = MANAGER_ROLE_NAMES.some(r => roleNames.some(name => name.includes(r)));
  const isManagerByPerm     = MANAGER_PERMISSIONS.some(p => permissions.includes(p));

  // Also handle the special activate_cards object format seen in the codebase
  const hasActivateCards = rolesList.some(r => (r?.permissions as any)?.activate_cards === 'FULL');
  const hasCustomRole = rolesList.length > 0;

  if (isManagerByJobRole || isManagerByRoleName || isManagerByPerm || hasActivateCards || hasCustomRole) {
    return 'MANAGER';
  }

  return 'NORMAL_USER';
}

// ─── Access Helpers ───────────────────────────────────────────────────────────

/**
 * Returns `true` when the given path (or a prefix of it) is in the role's
 * allowed list.  Works for both exact paths and sub-routes.
 */
export function hasAccess(user: any, path: string): boolean {
  const role = getUserRole(user);
  const allowed = ROLE_ACCESS[role];
  // Allow if the path starts with any allowed entry
  return allowed.some(p => path === p || path.startsWith(p + '/'));
}

/**
 * Returns the default landing path immediately after login.
 */
export function getLoginRedirect(user: any): string {
  const role = getUserRole(user);
  if (role === 'NORMAL_USER') return '/requestlist';
  return '/dashboard';
}
